import DataTable, {
  IDataTableRow,
  IHeadCell,
  Order,
} from "components/Common/DataTable";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Container,
  Paper,
  TableCell,
  Typography,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import AutoSendingReportToolbar, {
  AutoSendingReportFilters,
} from "../common/AutoSendingReportToolbar";
import Loading from "components/Common/Loading";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  loadZnsCampaignConfigurations,
  deactivateZnsCampaign,
  selectZnsCampaignConfigurationListResults,
  selectZnsCampaignConfigurationListTotalCount,
  selectZnsCampaignConfigurationListLoading,
  selectZnsCampaignDeactivateLoading,
  selectZnsCampaignDeactivateSuccessNonce,
} from "../store/znsCampaignSlice";
import {
  IZnsCampaignConfigurationListItem,
  IZnsCampaignConfigurationListParams,
  ZnsConfigurationReportSortBy,
  ZnsSendingMode,
} from "../types/znsCampaign";
import moment from "moment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";

const headCells: IHeadCell[] = [
  {
    id: "znsCampaignId",
    numeric: false,
    disablePadding: false,
    label: "ZNS Campaign ID",
    sortable: false,
  },
  {
    id: "znsCampaignName",
    numeric: false,
    disablePadding: false,
    label: "ZNS Campaign Name",
    sortable: false,
  },
  {
    id: "znsTemplateName",
    numeric: false,
    disablePadding: false,
    label: "Template Used",
    sortable: false,
  },
  {
    id: "isCurrent",
    numeric: false,
    disablePadding: false,
    label: "Condition Status",
    sortable: false,
  },
  {
    id: "completedScheduleSend",
    numeric: false,
    disablePadding: false,
    label: "Completed/Schedule Send",
    sortable: false,
  },
  {
    id: "znsSendStats",
    numeric: false,
    disablePadding: false,
    label: "ZNS OK/Failed",
    sortable: false,
  },
  {
    id: "targetAudience",
    numeric: false,
    disablePadding: false,
    label: "Recipient",
    sortable: false,
  },
  {
    id: "activeFrom",
    numeric: false,
    disablePadding: false,
    label: "Active from",
    sortable: true,
  },
  {
    id: "activeTo",
    numeric: false,
    disablePadding: false,
    label: "Active to",
    sortable: true,
  },
  {
    id: "createdDate",
    numeric: false,
    disablePadding: false,
    label: "Created On",
    sortable: true,
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "Actions",
    sortable: false,
  },
];

function formatDateTime(iso?: string) {
  if (!iso) return "—";
  return moment(iso).format("DD/MM/YYYY HH:mm");
}

function CurrentChip({ isCurrent }: { isCurrent: boolean }) {
  return (
    <Chip
      label={isCurrent ? "Active" : "Inactive"}
      variant="outlined"
      size="small"
      color={isCurrent ? "success" : "default"}
      sx={{ height: "24px" }}
    />
  );
}

function CampaignRow(props: IDataTableRow) {
  const item = props.item as IZnsCampaignConfigurationListItem | undefined;
  if (!item) return null;

  const stop = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      <TableCell>{item.znsCampaignId}</TableCell>
      <TableCell>{item.znsCampaignName}</TableCell>
      <TableCell sx={{ whiteSpace: "pre-wrap" }}>
        {item.znsTemplateName + " \nID:" + item.templateId}
      </TableCell>
      <TableCell>
        <CurrentChip isCurrent={item.isCurrent} />
      </TableCell>
      <TableCell>{/*item.completedScheduleSend*/}</TableCell>
      <TableCell>{/*item.znsSendStats*/}</TableCell>
      <TableCell>{item.targetAudience}</TableCell>
      <TableCell>{formatDateTime(item.activeFrom)}</TableCell>
      <TableCell>{formatDateTime(item.activeTo)}</TableCell>
      <TableCell>{formatDateTime(item.createdDate)}</TableCell>
      <TableCell align="center" onClick={stop}>
        <Stack direction="row" justifyContent="center">
          <Button
            size="small"
            variant="text"
            sx={{ minWidth: 24 }}
            onClick={(e) => {
              stop(e);
              props.onViewDetail?.(item);
            }}
          >
            <VisibilityIcon />
          </Button>
          {/* {item.sendingMode !== ZnsSendingMode.IMMEDIATE && ( */}
          <Button
            size="small"
            color="error"
            disabled={!item.isCurrent}
            variant="text"
            sx={{ minWidth: 24 }}
            onClick={(e) => {
              stop(e);
              props.onDelete?.(item);
            }}
          >
            <PauseCircleIcon />
          </Button>
          {/* )} */}
        </Stack>
      </TableCell>
    </>
  );
}

const DEFAULT_LIMIT = 15;

const DEFAULT_FILTERS: AutoSendingReportFilters = {
  searchTerm: "",
  znsCampaignName: "",
  znsCampaignId: "",
  znsTemplateId: "",
  templateId: "",
  sendingMode: "",
  isCurrent: "all",
  activeFromRange: { from: "", to: "" },
  activeToRange: { from: "", to: "" },
  createdOnRange: { from: "", to: "" },
};

const SORT_PROPERTY_TO_API: Record<string, ZnsConfigurationReportSortBy> = {
  activeFrom: "ActiveFrom",
  activeTo: "ActiveTo",
  sendingMode: "SendingMode",
  createdDate: "CreatedDate",
  modifiedDate: "ModifiedDate",
  version: "Version",
};

function buildConfigurationListParams(
  filters: AutoSendingReportFilters,
  page: number,
  limit: number,
  sortProperty: string,
  sortDirection: Order,
): IZnsCampaignConfigurationListParams {
  const sortBy = SORT_PROPERTY_TO_API[sortProperty] ?? "CreatedDate";

  let isCurrent: boolean | undefined;
  if (filters.isCurrent === "true") isCurrent = true;
  else if (filters.isCurrent === "false") isCurrent = false;

  return {
    PageNumber: page,
    PageSize: limit,
    SortBy: sortBy,
    SortDirection: sortDirection === "asc" ? "ASC" : "DESC",
    SearchTerm: filters.searchTerm.trim() || undefined,
    ZnsCampaignName: filters.znsCampaignName.trim() || undefined,
    ZnsCampaignId: filters.znsCampaignId.trim() || undefined,
    ZnsTemplateId: filters.znsTemplateId.trim() || undefined,
    TemplateId: filters.templateId.trim() || undefined,
    SendingMode: filters.sendingMode || undefined,
    IsCurrent: isCurrent,
    ActiveFromStart: filters.activeFromRange.from || undefined,
    ActiveFromEnd: filters.activeFromRange.to || undefined,
    ActiveToStart: filters.activeToRange.from || undefined,
    ActiveToEnd: filters.activeToRange.to || undefined,
    CreatedOnStart: filters.createdOnRange.from || undefined,
    CreatedOnEnd: filters.createdOnRange.to || undefined,
  };
}

const AutoSendingReportPage = () => {
  const dispatch = useAppDispatch();
  const results = useAppSelector(selectZnsCampaignConfigurationListResults);
  const totalCount = useAppSelector(
    selectZnsCampaignConfigurationListTotalCount,
  );
  const loading = useAppSelector(selectZnsCampaignConfigurationListLoading);
  const znsCampaignDeactivateLoading = useAppSelector(
    selectZnsCampaignDeactivateLoading,
  );
  const znsCampaignDeactivateSuccessNonce = useAppSelector(
    selectZnsCampaignDeactivateSuccessNonce,
  );

  const [viewItem, setViewItem] =
    useState<IZnsCampaignConfigurationListItem | null>(null);
  const [deactivateTarget, setDeactivateTarget] =
    useState<IZnsCampaignConfigurationListItem | null>(null);
  const prevDeactivateSuccessNonceRef = useRef(
    znsCampaignDeactivateSuccessNonce,
  );

  const [paging, setPagingData] = useState({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const [filters, setFilters] =
    useState<AutoSendingReportFilters>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<{ property: string; direction: Order }>({
    property: "createdDate",
    direction: "desc",
  });

  const queryParams = useMemo(
    () =>
      buildConfigurationListParams(
        filters,
        paging.page,
        paging.limit,
        sort.property,
        sort.direction,
      ),
    [filters, paging.page, paging.limit, sort.property, sort.direction],
  );

  const fetchConfigurations = useCallback(() => {
    dispatch(loadZnsCampaignConfigurations(queryParams));
  }, [dispatch, queryParams]);

  const handleConfirmDeactivate = useCallback(() => {
    if (!deactivateTarget?.znsCampaignId || !deactivateTarget?.id) return;
    dispatch(
      deactivateZnsCampaign({
        znsCampaignId: deactivateTarget.znsCampaignId,
        configurationId: deactivateTarget.id,
        refetchParams: queryParams,
      }),
    );
  }, [deactivateTarget, dispatch, queryParams]);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  useEffect(() => {
    if (
      znsCampaignDeactivateSuccessNonce > prevDeactivateSuccessNonceRef.current
    ) {
      prevDeactivateSuccessNonceRef.current = znsCampaignDeactivateSuccessNonce;
      setDeactivateTarget(null);
    }
  }, [znsCampaignDeactivateSuccessNonce]);

  const onPageChange = (pageIndexZeroBased: number) => {
    setPagingData((prev) => ({ ...prev, page: pageIndexZeroBased + 1 }));
  };

  const handleFilterChange = (newFilters: AutoSendingReportFilters) => {
    setFilters(newFilters);
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const handleSortChange = (property: string, direction: Order) => {
    setSort({ property, direction });
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <Container maxWidth={false}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Auto Sending Report
        </Typography>
      </Box>

      <Paper sx={{ width: "100%", mb: 2 }}>
        <AutoSendingReportToolbar
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleResetFilters}
        />

        {loading ? (
          <Loading />
        ) : (
          <DataTable
            hideToolbar={true}
            headCells={headCells}
            rows={results}
            paging={{
              total: totalCount,
              pages: Math.ceil(totalCount / paging.limit) || 1,
              page: paging.page - 1,
              limit: paging.limit,
            }}
            name="id"
            title=""
            sortable={true}
            order={sort.direction}
            orderBy={sort.property}
            onSortChange={handleSortChange}
            expandablePagination={true}
            onPageChange={onPageChange}
            onRowsPerPageChange={() => {}}
            onViewDetail={(row) =>
              setViewItem(row as IZnsCampaignConfigurationListItem)
            }
            onDelete={(row) =>
              setDeactivateTarget(row as IZnsCampaignConfigurationListItem)
            }
          >
            <CampaignRow />
          </DataTable>
        )}
      </Paper>

      <Dialog
        open={Boolean(viewItem)}
        onClose={() => setViewItem(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Conditions
          {viewItem?.znsCampaignName ? ` — ${viewItem.znsCampaignName}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {viewItem && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Configuration ID: {viewItem.id}
              </Typography>
              {viewItem.sendingMode && (
                <Typography variant="body2">
                  Sending mode: {viewItem.sendingMode}
                </Typography>
              )}
              {viewItem.cronExpression && (
                <Typography variant="body2">
                  Cron: {viewItem.cronExpression}
                </Typography>
              )}
              {viewItem.recipientFilePath && (
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  Recipients file: {viewItem.recipientFilePath}
                </Typography>
              )}
              <Typography variant="subtitle2">Rules</Typography>
              {viewItem.rules && viewItem.rules.length > 0 ? (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    fontSize: 12,
                    overflow: "auto",
                    maxHeight: 360,
                  }}
                >
                  {JSON.stringify(viewItem.rules, null, 2)}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No rules for this configuration.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewItem(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deactivateTarget)}
        onClose={() =>
          !znsCampaignDeactivateLoading && setDeactivateTarget(null)
        }
      >
        <DialogTitle>Deactivate ZNS campaign?</DialogTitle>
        <DialogContent>
          {deactivateTarget && (
            <Typography>
              This sets the configuration to inactive and marks campaign{" "}
              <strong>{deactivateTarget.znsCampaignName}</strong> (
              <strong>{deactivateTarget.znsCampaignId}</strong>) as{" "}
              <strong>DEACTIVE</strong>.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={znsCampaignDeactivateLoading}
            onClick={() => setDeactivateTarget(null)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={znsCampaignDeactivateLoading}
            onClick={handleConfirmDeactivate}
          >
            {znsCampaignDeactivateLoading ? "Deactivating…" : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default React.memo(AutoSendingReportPage);
